import * as Sentry from "@sentry/capacitor";

import type { ChatStore } from "@/Modules/Chat/Store";
import type { UserStore } from "@/Modules/User/Store";
import type { ChatRoom } from "@/Modules/Chat/Models/Domain/Chat";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";

import { useChatStore } from "@/Modules/Chat/Store";
import { useUserStore } from "@/Modules/User/Store";
import { useAuthStore, type AuthStore } from "@/Modules/Auth/Store";

import { transformChatApiToChatRoom } from "@/Modules/Chat/Transformers/Chat";

import { getChats, getChatEvents } from "@/Services/TherapyApi/Chat";
import { isApiError } from "@/Services/Api/Typeguards/isApiError";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { danger, log } from "@/Utils/Debug";

class ReconnectionWebSocketActionHandler {
  constructor(
    private readonly chatStore: ChatStore = useChatStore(),
    private readonly userStore: UserStore = useUserStore(),
    private readonly authStore: AuthStore = useAuthStore()
  ) {}

  public async handle() {
    log("Refresh chat info");
    this.chatStore.isReconnecting = true;

    if (!hasValue(this.userStore.user)) {
      this.chatStore.isReconnecting = false;

      return;
    }

    try {
      const hasChatSelected = isValidString(this.chatStore.activeChatRoomId);
      const chatsApi = await getChats().catch((e) => {
        if (isApiError(e)) {
          if (e.error_code === ApiErrorCodes.USER_LOGED_OUT) {
            this.authStore.logout();
          }
        }

        return undefined;
      });

      if (hasValue(chatsApi)) {
        const role = this.userStore.user.role;
        const chats = chatsApi.map((c) => transformChatApiToChatRoom(c, role, [], this.userStore.user!));
        const chatsMapExist: Record<string, true> = {};

        chats.forEach((c) => {
          chatsMapExist[c.id] = true;

          if (this.chatStore.rooms[c.id]) {
            log("Update chat", c.id);
            this.updateChatInfo(c);
          } else {
            log("Add chat", c.id);
            this.addChat(c);
          }
        });

        this.chatStore.chatRooms.forEach((c) => {
          if (!hasValue(chatsMapExist[c.id])) {
            log("Remove chat", c.id);
            this.removeChat(c);
          }
        });
      }

      // Check if active room is removed
      const isRemoved =
        hasChatSelected &&
        !isValidString(this.chatStore.activeChatRoomId) &&
        this.chatStore.chatRooms.length > 0;

      if (isRemoved) {
        await this.chatStore.setActiveChatRoom(this.chatStore.chatRooms[0].id);
      }

      // Check new messages on current chat
      const currentRoom = this.chatStore.getActiveRoom;
      const hasNewMessages = hasValue(currentRoom) && currentRoom.unreadEvents > 0;

      if (hasNewMessages) {
        const numEvents = currentRoom.unreadEvents;
        const chatId = currentRoom.id;
        const events = await getChatEvents(chatId, 0, numEvents).catch((e) => {
          if (isApiError(e)) {
            if (e.error_code === ApiErrorCodes.USER_LOGED_OUT) {
              this.authStore.logout();
            }
          }

          return undefined;
        });

        if (hasValue(events) && hasValue(this.chatStore.socketManager)) {
          await Promise.all(events.map((e) => this.chatStore.socketManager!.eventProcessor(e)));

          this.chatStore.rooms[currentRoom.id].unreadEvents = 0;
        }
      }
    } catch (e) {
      danger("Error in reconnect");
      Sentry.captureException(e);
    }

    this.chatStore.isReconnecting = false;
  }

  private updateChatInfo(chat: ChatRoom) {
    if (hasValue(this.chatStore.rooms) && hasValue(this.chatStore.rooms[chat.id])) {
      this.chatStore.rooms[chat.id].participants = chat.participants;
      this.chatStore.rooms[chat.id].isLoadingEvents = false;
      this.chatStore.rooms[chat.id].unreadEvents = chat.unreadEvents;
      this.chatStore.rooms[chat.id].lastEvent = chat.lastEvent;
    }

    if (this.chatStore.activeChatRoomId !== chat.id) {
      this.chatStore.rooms[chat.id].events = [];
    }
  }

  private addChat(chat: ChatRoom) {
    this.chatStore.rooms[chat.id] = chat;
  }

  private removeChat(chat: ChatRoom) {
    if (this.chatStore.activeChatRoomId === chat.id) {
      this.chatStore.activeChatRoomId = undefined;
    }

    delete this.chatStore.rooms[chat.id];
  }
}

export default ReconnectionWebSocketActionHandler;
